import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@repo/ui/components/ui/form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui/components/ui/accordion";
import { useLocale, useTranslations } from "next-intl";
import { useEffect } from "react";
import { Gender, GenderItem, getGenderFilter } from "@/types/product-search";
import { useAutoSubmit } from "@/hooks/useAutoSubmit";
import { Checkbox } from "@repo/ui/components/ui/checkbox";
import { cn } from "@repo/ui/lib/utils";

const FormOrderSchema = z.object({
  gender: z.array(z.string()),
});

export const FiltersMenuItemGender = ({
  initialValue,
  applyFilter,
}: {
  applyFilter: (data: { gender: string[] }) => void;
  initialValue?: Array<keyof Gender>;
}) => {
  const form = useForm<z.infer<typeof FormOrderSchema>>({
    resolver: zodResolver(FormOrderSchema),
    defaultValues: {
      gender: [],
    },
  });

  const t = useTranslations();
  const genderKeys = ["man", "woman", "child"];

  // function findTopLevelKeyByValue(value: string): string | undefined {
  //   for (const topKey in genderBase) {
  //     const nested = genderBase[topKey as keyof Gender];
  //     for (const lang in nested) {
  //       if (nested[lang as keyof GenderItem] === value) {
  //         return topKey;
  //       }
  //     }
  //   }
  //   return "";
  // }

  function onSubmit(data: z.infer<typeof FormOrderSchema>) {
    applyFilter(data);
  }

  useEffect(() => {
    if (initialValue) {
      form.setValue("gender", initialValue);
    }
  }, [initialValue]);

  useAutoSubmit({
    trigger: form.trigger,
    watch: form.watch,
    onSubmit: form.handleSubmit(onSubmit),
  });

  const locale = useLocale().substring(0, 2) as keyof GenderItem;

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="item-1">
        <div className="py-[16px]">
          <AccordionTrigger className="max-md:text-base font-medium py-0 data-[state=open]:text-primary-magenta">
            {t("Products-list.filters.gender")}
          </AccordionTrigger>
          <AccordionContent className="mt-[20px] p-0">
            <div className="pb-6 px-0 text-left w-full">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <FormField
                    control={form.control}
                    name="gender"
                    render={() => (
                      <FormItem>
                        <div className="flex flex-col items-start gap-2">
                          {genderKeys.map((item: string) => (
                            <FormField
                              key={item}
                              control={form.control}
                              name="gender"
                              render={({ field }) => {
                                return (
                                  <FormItem
                                    className={cn("*:text-[14px]", {
                                      "font-bold": field.value.includes(item),
                                    })}
                                  >
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value.includes(item)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            field.onChange([
                                              ...field.value,
                                              item,
                                            ]);
                                          } else {
                                            field.onChange(
                                              field.value.filter(
                                                (value) => value !== item,
                                              ),
                                            );
                                          }
                                        }}
                                      >
                                        {getGenderFilter(
                                          locale,
                                          item as keyof Gender,
                                        )}
                                      </Checkbox>
                                    </FormControl>
                                  </FormItem>
                                );
                              }}
                            />
                          ))}
                        </div>
                      </FormItem>
                    )}
                  />
                </form>
              </Form>
            </div>
          </AccordionContent>
        </div>
      </AccordionItem>
    </Accordion>
  );
};
